import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { BiEdit } from "react-icons/bi";
import { IoTrashOutline } from "react-icons/io5";
import PageHeader from "../../component/pageHeaders";
import { API } from "../../config/API";
import { DELETE, GET, PATCH, POST, PUT } from "../../utils/apirequest";
import { CountryDto } from "../../config/countries";
import { RegionDto } from "../../config/regions";
import { loadEpaperConfig } from "../../config/epaperConfig";
import { apiErrorMessage } from "./utils";
import "./styles.css";

type FormValues = {
  name: string;
  code: string;
  currency_name: string;
  currency_code: string;
  dial_code: string;
  default_amount: number;
  is_active: boolean;
  sort_order: number;
  region_id?: number | null;
};

function EpaperCountries() {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [rows, setRows] = useState<CountryDto[]>([]);
  const [regions, setRegions] = useState<RegionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<CountryDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = async () => {
    setLoading(true);
    try {
      const [res, r]: any[] = await Promise.all([
        GET(API.EPAPER_SETTINGS_COUNTRIES, null, null),
        GET(API.EPAPER_SETTINGS_REGIONS, null, null),
      ]);
      setRows(Array.isArray(res?.data) ? res.data : []);
      setRegions(Array.isArray(r?.data) ? r.data : []);
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not load countries."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Keep the shared lists used by the e-paper forms in step.
  const afterChange = async () => {
    await Promise.all([load(), loadEpaperConfig(true)]);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.code, r.iso3, r.currency_code, r.currency_name, r.dial_code, r.region_name]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [rows, search]);

  // Back to the first page when the search changes; stay in range after a delete.
  useEffect(() => {
    setPage(1);
  }, [search]);
  const currentPage = Math.min(page, Math.max(1, Math.ceil(filtered.length / pageSize)));
  const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Regions in display order. The country's current region stays listed
  // (disabled) even if since deactivated, so editing shows what it was.
  const regionOptions = [...regions]
    .filter((r) => r.is_active || r.id === editing?.region_id)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
    .map((r) => ({
      value: r.id,
      label: `${r.name}${r.is_active ? "" : " (inactive)"}`,
      disabled: !r.is_active,
    }));

  const validateRegion = (_: unknown, value?: number | null) => {
    if (value === undefined || value === null) {
      return Promise.reject(new Error("Region is required"));
    }
    const region = regions.find((r) => r.id === value);
    if (!region) return Promise.reject(new Error("Please select a valid region"));
    if (!region.is_active) {
      return Promise.reject(new Error(`${region.name} is inactive. Please select an active region`));
    }
    return Promise.resolve();
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      sort_order: rows.length + 1,
      dial_code: "",
    } as any);
    setModalOpen(true);
  };

  const openEdit = (row: CountryDto) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      ...row,
      currency_name: row.currency_name || "",
      dial_code: String(row.dial_code || "").replace(/^\+/, ""),
      region_id: row.region_id ?? undefined,
    });
    setModalOpen(true);
  };

  const onSave = async (values: FormValues) => {
    setSaving(true);
    const payload = {
      data: {
        ...values,
        code: String(values.code || "").trim().toUpperCase(),
        currency_name: String(values.currency_name || "").trim(),
        currency_code: String(values.currency_code || "").trim().toUpperCase(),
        dial_code: `+${String(values.dial_code || "").replace(/^\+/, "").trim()}`,
        region_id: values.region_id,
      },
    };
    try {
      if (editing) {
        await PUT(`${API.EPAPER_SETTINGS_COUNTRIES}/${editing.id}`, payload);
        message.success("Country updated");
      } else {
        await POST(API.EPAPER_SETTINGS_COUNTRIES, payload);
        message.success("Country added");
      }
      setModalOpen(false);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not save country."));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row: CountryDto, active: boolean) => {
    setTogglingId(row.id);
    try {
      await PATCH(`${API.EPAPER_SETTINGS_COUNTRIES}/${row.id}/status`, {
        data: { is_active: active },
      });
      message.success(`${row.name} ${active ? "enabled" : "disabled"}`);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not update status."));
    } finally {
      setTogglingId(null);
    }
  };

  const remove = async (row: CountryDto) => {
    try {
      await DELETE(`${API.EPAPER_SETTINGS_COUNTRIES}/${row.id}`);
      message.success(`${row.name} deleted`);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not delete country."));
    }
  };

  const columns: ColumnsType<CountryDto> = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_t, _r, i) => (currentPage - 1) * pageSize + i + 1,
    },
    {
      title: "Country",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Region",
      dataIndex: "region_name",
      key: "region_name",
      width: 120,
      render: (name: string) => name || "—",
    },
    { title: "Country Code", dataIndex: "code", key: "code", width: 120 },
    { title: "Phone Code", dataIndex: "dial_code", key: "dial_code", width: 110 },
    {
      title: "Currency",
      dataIndex: "currency_code",
      key: "currency_code",
      width: 110,
      render: (code: string, row) => (
        <Tooltip title={row.currency_name || undefined}>{code}</Tooltip>
      ),
    },
    {
      title: "Default Amount",
      dataIndex: "default_amount",
      key: "default_amount",
      width: 130,
    },
    {
      title: "Zones",
      dataIndex: "zone_count",
      key: "zone_count",
      width: 80,
      render: (n: number) => n || "—",
    },
    {
      title: "Status",
      key: "is_active",
      width: 140,
      render: (_t, row) => (
        <Space>
          <Switch
            size="small"
            checked={row.is_active}
            loading={togglingId === row.id}
            onChange={(checked) => toggleStatus(row, checked)}
          />
          <span>{row.is_active ? "Active" : "Inactive"}</span>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_t, row) => (
        <Space size="middle">
          <BiEdit size={20} color="#f5a442" cursor="pointer" onClick={() => openEdit(row)} />
          <Popconfirm
            title="Delete Country"
            description={`Delete ${row.name}? Countries in use can only be disabled.`}
            okText="Yes"
            cancelText="No"
            onConfirm={() => remove(row)}
          >
            <IoTrashOutline size={20} color="red" cursor="pointer" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={"Countries"}
        loader={loading}
        length={"Dashboard / E-Paper Settings / Countries"}
        onBack={() => navigate("/Auth/epaper-settings")}
      >
        <div className="homescreen-pageItem">
          <Input.Search
            style={{ width: 250 }}
            allowClear
            placeholder="Search country..."
            onChange={(e) => setSearch(e.target.value)}
          />
          &nbsp;
          <Button type="primary" onClick={openCreate}>
            + Add Country
          </Button>
        </div>
      </PageHeader>
      <div className="epaperSettings-subtitle" style={{ paddingTop: 10 }}>
        Manage countries, currency, phone code and default subscription amount.
      </div>
      <Table
        columns={columns}
        dataSource={pageRows}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
      />
      <br />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Pagination
          responsive
          current={currentPage}
          total={filtered.length}
          pageSize={pageSize}
          onChange={(p, ps) => {
            setPage(p);
            setPageSize(ps);
          }}
        />
      </div>
      <br />

      <Modal
        title={editing ? "Edit Country" : "Add Country"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={680}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSave} requiredMark>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label="Region"
                name="region_id"
                extra="Selected automatically for agents in this country"
                rules={[{ required: true, validator: validateRegion }]}
              >
                <Select
                  placeholder="Select region"
                  showSearch
                  optionFilterProp="label"
                  options={regionOptions}
                  notFoundContent="No active regions. Add one in E-Paper Settings → Regions"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Country Name"
                name="name"
                rules={[{ required: true, whitespace: true, message: "Country name is required" }]}
              >
                <Input placeholder="UAE" maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Country Code"
                name="code"
                extra={editing ? "Cannot be changed after creation" : undefined}
                rules={[
                  { required: true, message: "Country code is required" },
                  { pattern: /^[A-Za-z]{2}$/, message: "2 letters (ISO alpha-2), e.g. AE" },
                ]}
              >
                <Input placeholder="AE" maxLength={2} disabled={!!editing} style={{ textTransform: "uppercase" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Currency Name" name="currency_name">
                <Input placeholder="UAE Dirham" maxLength={60} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Currency Code"
                name="currency_code"
                rules={[
                  { required: true, message: "Currency code is required" },
                  { pattern: /^[A-Za-z]{3}$/, message: "3 letters, e.g. AED" },
                ]}
              >
                <Input placeholder="AED" maxLength={3} style={{ textTransform: "uppercase" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Default Subscription Amount"
                name="default_amount"
                extra="Charged when a paid plan has no price for this country"
                rules={[{ required: true, message: "Default amount is required" }]}
              >
                <InputNumber min={0} precision={2} style={{ width: "100%" }} placeholder="100" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Phone Code"
                name="dial_code"
                rules={[
                  { required: true, message: "Phone code is required" },
                  { pattern: /^\+?\d{1,4}$/, message: "1–4 digits, e.g. 971" },
                ]}
              >
                <Input addonBefore="+" placeholder="971" maxLength={5} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Status" name="is_active" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value={true}>Active</Radio>
                  <Radio value={false}>Inactive</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Display Order"
                name="sort_order"
                rules={[{ type: "integer", min: 0, message: "Whole number ≥ 0" }]}
              >
                <InputNumber min={0} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default EpaperCountries;
